/*
 * v3 world flag (docs/DESIGN-BRIEF.md "Rollout", docs/WEB-PRD.md).
 * The IA is reconfigured, so the swap grain is the WORLD, not the page:
 *   in the v3 world = the new chrome, routes, and areas;
 *   out of it        = classic, untouched.
 * Resolution: dev-toolbar world override ?? WEB_V3 feature flag.
 * Inside the world, areas are either NATIVE (built) or BRIDGE (their nav
 * row links out to the classic surface at its old URL). V3_NATIVE is
 * code-owned: an area joins when it fully replaces its classic ground.
 */

export const WEB_V3_FLAG = 'WEB_V3';

export interface V3Area {
    id: string;
    label: string;
    /** v3 route (canonical, no /v3 prefix) */
    route: string;
    /** classic URL the bridge points at while the area isn't native */
    classicUrl?: string;
}

/*
 * The app-level areas (PRD: app level). classicUrl = where the bridge
 * lands today: the v1 surface where one exists, else the live v2 surface
 * (linking is not importing — README rule 4 is about code).
 * Areas with no ground at all (Tasks, Assets, Software) are NOT listed:
 * they appear in the rail only once they're native — no dead rows.
 */
export const V3_AREAS: V3Area[] = [
    { id: 'home', label: 'Home', route: '/home' },
    {
        id: 'activations',
        label: 'Activations',
        route: '/activations',
        classicUrl: '/activations',
    },
    {
        id: 'devices',
        label: 'Devices',
        /* native at its existing URL */
        route: '/device-management',
        classicUrl: '/device-management',
    },
    {
        id: 'pipeline',
        label: 'Pipeline',
        route: '/pipeline',
        classicUrl: '/jobs',
    },
    {
        id: 'design',
        label: 'Design',
        route: '/design',
        classicUrl: '/v2/jobs/design',
    },
    {
        id: 'staff',
        label: 'Staff',
        route: '/staff',
        classicUrl: '/v2/jobs/staff',
    },
    { id: 'gear', label: 'Gear', route: '/gear', classicUrl: '/v2/jobs/gear' },
    {
        id: 'venues',
        label: 'Venues',
        route: '/venues',
        classicUrl: '/v2/jobs/venues',
    },
    {
        id: 'contacts',
        label: 'Contacts',
        route: '/contacts',
        classicUrl: '/v2/jobs/contacts',
    },
];

/*
 * Cluster rhythm for the rail/sheet (brief: whitespace-only clusters,
 * ordered by frequency). Clusters shrink while their areas lack ground;
 * Tasks joins cluster 2, Assets/Software cluster 4, when they exist.
 */
export const APP_CLUSTERS: ReadonlyArray<ReadonlyArray<string>> = [
    ['home', 'activations', 'devices'],
    ['pipeline', 'design'],
    ['staff', 'gear', 'venues', 'contacts'],
    /* Favorites hidden as unused; classic /favorites still exists */
];

/* Code-owned: area ids that are native (no bridge). */
export const V3_NATIVE: ReadonlyArray<string> = [
    'activations',
    'devices',
    'pipeline',
    'design',
    'staff',
    'gear',
    'venues',
    'contacts',
];

export function areaTarget(area: V3Area): string {
    return isNative(area.id) ? area.route : (area.classicUrl ?? area.route);
}

const WORLD_KEY = 'ps.v3.world';
export const OVERRIDES_EVENT = 'ps-v3-overrides';

/** true = forced v3, false = forced classic, undefined = follow the flag */
export function getWorldOverride(): boolean | undefined {
    try {
        const raw = window.localStorage.getItem(WORLD_KEY);
        return raw === null ? undefined : raw === '1';
    } catch {
        return undefined;
    }
}

export function setWorldOverride(value: boolean | null): void {
    try {
        if (value === null) window.localStorage.removeItem(WORLD_KEY);
        else window.localStorage.setItem(WORLD_KEY, value ? '1' : '0');
    } catch {
        /* storage unavailable — override just doesn't persist */
    }
    window.dispatchEvent(new Event(OVERRIDES_EVENT));
}

export function isV3World(flagOn: boolean): boolean {
    const override = getWorldOverride();
    return override !== undefined ? override : flagOn;
}

export function isNative(areaId: string): boolean {
    return V3_NATIVE.includes(areaId);
}
